import { z } from 'zod';

/**
 * Catalogo de telas moduláveis.
 *
 * Fica no pacote compartilhado, e nao em cada lado, porque a mesma lista
 * governa tres coisas que precisam concordar: as caixas de selecao no cadastro
 * de usuario, o menu que o front monta e a checagem que a API faz antes de
 * responder. Se cada um tivesse a sua copia, a tela sumiria do menu mas a rota
 * continuaria respondendo — que e o pior dos dois mundos.
 *
 * Configuracoes e Usuarios NAO entram aqui de proposito: sao restritas a
 * administrador pelo papel, e oferecer uma caixa de selecao que nao muda nada
 * para um colaborador seria mentir sobre o que o controle faz.
 */
export const SCREENS = [
  {
    key: 'home',
    label: 'Dashboard',
    description: 'Pendencias do banco, indicadores de despesa e evolucao.',
    path: '/',
  },
  {
    key: 'extratos',
    label: 'Extratos',
    description: 'Importacao de OFX e historico de arquivos.',
    path: '/extratos',
  },
  {
    key: 'classificacao',
    label: 'Classificacao',
    description: 'Classificar lancamentos e anexar comprovantes.',
    path: '/classificacao',
  },
  {
    key: 'plano',
    label: 'Plano financeiro',
    description: 'Grupos e categorias que a classificacao oferece.',
    path: '/plano',
  },
  {
    key: 'bancos_contabeis',
    label: 'Bancos contabeis',
    description: 'Codigo contabil de cada banco, usado na exportacao.',
    path: '/bancos-contabeis',
  },
  {
    key: 'patrimoniais',
    label: 'Contas patrimoniais',
    description: 'Contas de balanco que a provisao usa como contrapartida.',
    path: '/patrimoniais',
  },
  {
    key: 'exportacao',
    label: 'Exportacao contabil',
    description: 'Partidas dobradas dos lancamentos, em XLSX.',
    path: '/exportacao',
  },
  {
    key: 'planilhas',
    label: 'Planilhas',
    description: 'Planilhas de recebimento enviadas ao longo do mes.',
    path: '/planilhas',
  },
  {
    key: 'relatorios',
    label: 'DRE',
    description: 'Demonstrativo de resultado por competencia.',
    path: '/relatorios',
  },
  {
    key: 'balanco',
    label: 'Demonstração contábil',
    description: 'Balanço patrimonial e demonstrações a partir do balancete.',
    path: '/balanco',
  },
] as const;

export type ScreenKey = (typeof SCREENS)[number]['key'];

export const SCREEN_KEYS: readonly ScreenKey[] = SCREENS.map((screen) => screen.key);

/**
 * Funcionalidades temporariamente fora do produto. A chave continua valida e
 * as permissoes permanecem no banco, portanto reativar nao exige migration nem
 * recuperacao de dados: basta retirar a chave desta lista.
 */
export const INACTIVE_SCREEN_KEYS: readonly ScreenKey[] = ['relatorios'];

export function telaAtiva(key: ScreenKey): boolean {
  return !INACTIVE_SCREEN_KEYS.includes(key);
}

export const SCREENS_ATIVAS = SCREENS.filter((screen) => telaAtiva(screen.key));

export const screenKey = z.enum(
  SCREEN_KEYS as unknown as [ScreenKey, ...ScreenKey[]],
);

/**
 * Telas de um usuario novo. E o conjunto operacional inteiro: o padrao de um
 * sistema de permissao deve ser o que a maioria precisa, para que restringir
 * seja um ato deliberado e visivel, nao um esquecimento.
 */
export const SCREENS_PADRAO: readonly ScreenKey[] = SCREENS_ATIVAS.map((screen) => screen.key);

export function ehScreenKey(value: unknown): value is ScreenKey {
  return typeof value === 'string' && (SCREEN_KEYS as readonly string[]).includes(value);
}

/** Admin enxerga tudo: e o papel que administra as permissoes dos outros. */
export function telasDoUsuario(role: string, liberadas: readonly string[]): ScreenKey[] {
  const ativas = SCREENS_ATIVAS.map((screen) => screen.key);
  if (role === 'admin') return ativas;
  return ativas.filter((key) => liberadas.includes(key));
}
